import { FormEvent, FormHTMLAttributes, ReactElement, ReactNode, useMemo } from 'react'
import { FormProvider, UseFormReturn } from 'react-hook-form'
import { LabeledCheckbox } from 'components/atoms/inputs/LabeledCheckbox/LabeledCheckbox'
import { LabeledInput } from 'components/atoms/inputs/LabeledInput/LabeledInput'
import { CCCreatePaymentApiRequest, CCCreatePaymentMethodApiRequest } from 'components/features/creditCard/creditCardStore/types'
import { IS_MOBILE } from 'helpers/userAgentHelpers'
import { DevTool } from '@hookform/devtools'
import { ExampleFormType } from 'components/utils/styleguide/SGFormFields'
import { FlexOptions } from 'components/atoms/Flex/Flex'
import { useFlexClassNames } from 'hooks/useClassNames'
import { Preferences } from 'components/features/preferences/Preferences'
import {
  CryptoAutoInvestFields,
} from 'components/features/rewards/rewardsPane/containers/CryptoRewardsBuyPane/CryptoRewardsBuyAmountStep/CryptoRewardsBuyAmountStep'
import {
  CryptoSellFields,
} from 'components/features/rewards/rewardsPane/containers/CryptoRewardsSellPane/CryptoRewardsSellModeSellAmountStep/CryptoRewardsSellModeSellAmountStep'
import { VeritfyCodeForm } from 'components/features/creditCard/creditCardOnboarding/PhoneStepVerifyForm/PhoneStepVerifyForm'
import { ReplaceCardReasonForm } from 'components/features/creditCard/creditCardPanes/ReplaceCardPane'
import { FutureLayer } from 'components/features/future/types'

export type WithEmailField = {
  email: string
}

export type FormType =
| CCCreatePaymentApiRequest
| CCCreatePaymentMethodApiRequest
| ExampleFormType
| WithEmailField
| Preferences
| CryptoAutoInvestFields
| CryptoSellFields
| VeritfyCodeForm
| ReplaceCardReasonForm
| FutureLayer

export type FormPresetProps<Type extends FormType> = {
  flexOptions?: FlexOptions
  formContext: UseFormReturn<Type>
  formProps?: Omit<FormHTMLAttributes<HTMLFormElement>, 'style'>
  children: ReactNode
  debug?: boolean
  onSubmit?: (event: FormEvent<HTMLFormElement>) => void
}

// type FormInputFieldsPreset = {
//   Input: typeof LabeledInput,
//   Checkbox: typeof LabeledCheckbox
// }

// type FormWithInputsPreset<Type extends FormType> = FC<FormPresetProps<Type>> & FormInputFieldsPreset

export function FormPreset<Type extends FormType> (props: FormPresetProps<Type>): ReactElement {
  const {
    debug,
    flexOptions,
    formProps,
    formContext,
    children,
    onSubmit = (event) => { event.preventDefault() },
  } = props

  const devTool = useMemo(() => !!debug && !IS_MOBILE && (
    <DevTool placement='top-right' control={ formContext.control } />
  ), [debug, formContext.control])

  const classNames = useFlexClassNames(flexOptions)

  return (
    <FormProvider { ...formContext }>
      <form { ...formProps } onSubmit={ onSubmit } className={ classNames }>
        { children }
      </form>
      { devTool }
    </FormProvider>
  )
}

FormPreset.LabeledInput = LabeledInput
FormPreset.Checkbox = LabeledCheckbox
